import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRegisterWithCompanyMutation } from "../../services/api";
import { setCredentials } from "../../store/slices/authSlice";
import ErrorMessage from "../../Components/common/ErrorMessage";
import "./CreateCompany.css";

const CreateCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [registerWithCompany, { isLoading, error }] = useRegisterWithCompanyMutation();

  const [formData, setFormData] = useState({
    // User fields
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",

    // Company fields
    companyName: "",
    companySlug: "",
    companyDescription: "",
  });

  const [validationError, setValidationError] = useState("");

  // Optional positions during company creation
  const [showPositionsSection, setShowPositionsSection] = useState(false);
  const [positions, setPositions] = useState([]);
  const [newPositionName, setNewPositionName] = useState("");

  const handleAddPosition = () => {
    const trimmed = newPositionName.trim();
    if (trimmed && !positions.some(p => p.name.toLowerCase() === trimmed.toLowerCase())) {
      setPositions([...positions, { name: trimmed }]);
      setNewPositionName("");
    }
  };

  const handleRemovePosition = (index) => {
    setPositions(positions.filter((_, i) => i !== index));
  };

  const handlePositionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddPosition();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Auto-generate slug from company name
    if (name === "companyName") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      setFormData(prev => ({ ...prev, companySlug: slug }));
    }

    // Clear validation error when user starts typing
    if (validationError) setValidationError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setValidationError("Password must be at least 8 characters");
      return;
    }

    if (!formData.companyName.trim()) {
      setValidationError("Company name is required");
      return;
    }

    if (!formData.companySlug.trim()) {
      setValidationError("Company slug is required");
      return;
    }

    try {
      const payload = {
        user: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
        },
        company: {
          name: formData.companyName,
          slug: formData.companySlug,
          description: formData.companyDescription || undefined,
          positions: positions.length > 0 ? positions : undefined,
        },
      };

      const response = await registerWithCompany(payload).unwrap();

      // Auto-login after successful registration
      if (response.user && response.company && response.tokens) {
        dispatch(setCredentials(response));
        navigate("/dashboard");
      }
    } catch (err) {
      console.error("Registration failed:", err);
      // Error handled by RTK Query
    }
  };

  return (
    <div className="create-company-page">
      <div className="create-company-container">
        <div className="create-company-card">
          <div className="create-company-header">
            <h1>Create Your Company</h1>
            <p>Set up your organization and get started</p>
          </div>

          {error && <ErrorMessage error={error} />}
          {validationError && (
            <div className="validation-error">{validationError}</div>
          )}

          <form onSubmit={handleSubmit} className="create-company-form">
            {/* Company Information Section */}
            <div className="form-section">
              <h3>Company Information</h3>

              <div className="form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="companySlug">
                  Company URL Slug *
                  <span className="help-text">(auto-generated, can be modified)</span>
                </label>
                <div className="slug-input-group">
                  <span className="slug-prefix">yourapp.com/</span>
                  <input
                    type="text"
                    id="companySlug"
                    name="companySlug"
                    value={formData.companySlug}
                    onChange={handleChange}
                    placeholder="company-slug"
                    required
                    pattern="[-a-z0-9]+"
                    title="Only lowercase letters, numbers, and hyphens allowed"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="companyDescription">Description (Optional)</label>
                <textarea
                  id="companyDescription"
                  name="companyDescription"
                  value={formData.companyDescription}
                  onChange={handleChange}
                  placeholder="Brief description of your company"
                  rows="3"
                />
              </div>

              {/* Optional Team Roles Section */}
              <div className="positions-setup-section">
                <button
                  type="button"
                  className="toggle-positions-btn"
                  onClick={() => setShowPositionsSection(!showPositionsSection)}
                >
                  {showPositionsSection ? "− Hide" : "+ Add"} Team Roles (Optional)
                </button>

                {showPositionsSection && (
                  <div className="positions-setup">
                    <p className="positions-help-text">
                      Create roles for your team members to select when joining (e.g., Chef, Manager, Server)
                    </p>

                    <div className="add-position-row">
                      <input
                        type="text"
                        value={newPositionName}
                        onChange={(e) => setNewPositionName(e.target.value)}
                        onKeyDown={handlePositionKeyDown}
                        placeholder="Enter role name"
                        maxLength={100}
                      />
                      <button
                        type="button"
                        onClick={handleAddPosition}
                        disabled={!newPositionName.trim()}
                        className="btn-add-role"
                      >
                        Add
                      </button>
                    </div>

                    {positions.length > 0 && (
                      <ul className="positions-preview-list">
                        {positions.map((pos, idx) => (
                          <li key={idx}>
                            <span>{pos.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemovePosition(idx)}
                              className="btn-remove-role"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {positions.length === 0 && (
                      <p className="no-positions-text">No roles added yet. You can add them later from the Team page.</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Owner/Admin Information Section */}
            <div className="form-section">
              <h3>Your Information (Company Owner)</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1234567890"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="create-company-btn"
              disabled={isLoading}
            >
              {isLoading ? "Creating company..." : "Create Company & Get Started"}
            </button>
          </form>

          <div className="create-company-footer">
            <p>
              Already have a company?{" "}
              <Link to="/join-company" className="join-link">
                Join with invite code
              </Link>
            </p>
            <p>
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCompany;
